import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../lib/tenant";
import { TopBar } from "../components/TopBar";
import { useAmbassadorDashboard } from "../lib/ambassador";
import { api, callableErrorMessage } from "../lib/api";
import { APP_URL } from "../lib/config";
import { AmbassadorShareKit } from "../components/AmbassadorShareKit";

export function AmbassadorDashboard() {
  const nav = useNavigate();
  const { tenant } = useTenant();
  const { loading, ambassador, codes, commissions } = useAmbassadorDashboard();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [busyToggle, setBusyToggle] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const onGenerate = async () => {
    setGenerating(true);
    setFeedback(null);
    try {
      const res = await api.generateAmbassadorCode({});
      setFeedback(`Nuevo código creado: ${res.data.code}`);
      setTimeout(() => setFeedback(null), 3000);
    } catch (err) {
      setFeedback(`✗ ${callableErrorMessage(err)}`);
    } finally {
      setGenerating(false);
    }
  };

  const onToggle = async (code: string, active: boolean) => {
    setBusyToggle(code);
    try {
      await api.toggleDiscountCode({ code, active });
    } catch (err) {
      setFeedback(`✗ ${callableErrorMessage(err)}`);
    } finally {
      setBusyToggle(null);
    }
  };

  const formatCLP = (n: number) =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      maximumFractionDigits: 0
    }).format(n);

  const totalUses = codes.reduce((acc, c) => acc + c.timesUsed, 0);

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch {
      /* ignore */
    }
  };

  const shareCode = async (code: string) => {
    const url = `${APP_URL}/?join=${tenant?.tenantId ?? ""}&ref=${code}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${tenant?.name ?? "Fideliza"} — código de descuento`,
          text: `Usa mi código ${code} en ${tenant?.name ?? "el local"} y llévate descuento 🎁`,
          url
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyCode(code);
    }
  };

  return (
    <>
      <TopBar />
      <main className="bg-cream pb-16 pt-4">
        <div className="mx-auto flex max-w-md flex-col gap-5 px-5">
          <header>
            <button onClick={() => nav("/")} className="btn-ghost mb-3 px-3 py-1.5 text-xs">
              ← Volver
            </button>
            <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Panel embajador</p>
            <h1 className="mt-1 font-display text-2xl font-semibold text-leaf-950">
              {ambassador?.name ?? "Embajador"}
            </h1>
            <p className="mt-1 text-sm text-leaf-700">
              Tus referidos en {tenant?.name ?? "el comercio"}.
            </p>
          </header>

          {loading && <p className="text-sm text-leaf-700">Cargando…</p>}

          <section className="grid grid-cols-3 gap-2">
            <StatCard label="Comisión" value={formatCLP(ambassador?.totalCommissions ?? 0)} highlight />
            <StatCard label="Ventas" value={formatCLP(ambassador?.totalSales ?? 0)} />
            <StatCard label="Usos" value={String(totalUses)} />
          </section>

          <section className="card flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-wider text-leaf-600">Tus códigos</p>
                <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                  Compártelos en tus redes
                </h2>
              </div>
              <button
                onClick={onGenerate}
                disabled={generating}
                className="btn-primary px-3 py-1.5 text-xs disabled:opacity-50"
              >
                {generating ? "…" : "+ Generar"}
              </button>
            </div>

            {feedback && (
              <p className="rounded-xl bg-leaf-50 px-3 py-2 text-xs text-leaf-800">{feedback}</p>
            )}

            {codes.length === 0 ? (
              <EmptyBox
                title="Aún no tienes códigos"
                body="Cuando te apruebe el comercio se genera tu primer código automáticamente."
              />
            ) : (
              codes.map((dc) => (
                <div
                  key={dc.id}
                  className={`flex items-center justify-between gap-3 rounded-2xl border-2 p-3 ${
                    dc.active ? "border-leaf-200 bg-leaf-50/50" : "border-leaf-100 bg-gray-50 opacity-60"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-base font-semibold text-leaf-950">{dc.code}</p>
                    <p className="text-xs text-leaf-700">
                      {dc.discountPercent}% off · {dc.timesUsed} {dc.timesUsed === 1 ? "uso" : "usos"}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      onClick={() => copyCode(dc.code)}
                      className="rounded-lg bg-white p-2 text-leaf-700 ring-1 ring-leaf-200 hover:bg-leaf-50"
                      title="Copiar"
                    >
                      {copiedCode === dc.code ? "✓" : "📋"}
                    </button>
                    <button
                      onClick={() => shareCode(dc.code)}
                      className="rounded-lg bg-white p-2 text-leaf-700 ring-1 ring-leaf-200 hover:bg-leaf-50"
                      title="Compartir"
                    >
                      ↗
                    </button>
                    <button
                      onClick={() => onToggle(dc.code, !dc.active)}
                      disabled={busyToggle === dc.code}
                      className="rounded-lg bg-white p-2 text-xs text-leaf-700 ring-1 ring-leaf-200 hover:bg-leaf-50 disabled:opacity-50"
                      title={dc.active ? "Desactivar" : "Activar"}
                    >
                      {busyToggle === dc.code ? "…" : dc.active ? "🟢" : "⚪"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </section>

          {codes.filter((c) => c.active).slice(0, 1).map((dc) => (
            <AmbassadorShareKit
              key={`kit-${dc.code}`}
              code={dc.code}
              tenantId={tenant?.tenantId ?? ""}
              tenantName={tenant?.name ?? "tu cafe"}
              discountPercent={dc.discountPercent}
            />
          ))}

          <section className="card flex flex-col gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-leaf-600">Historial</p>
              <h2 className="mt-1 font-display text-lg font-semibold text-leaf-950">
                Comisiones recibidas
              </h2>
            </div>

            {commissions.length === 0 ? (
              <EmptyBox
                title="Aún no tienes comisiones"
                body="Cuando alguien use tu código en una compra, vas a ver el detalle aquí."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-leaf-600">
                    <tr>
                      <th className="py-2 text-left">Fecha</th>
                      <th className="py-2 text-left">Código</th>
                      <th className="py-2 text-right">Venta</th>
                      <th className="py-2 text-right">Comisión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c) => (
                      <tr key={c.id} className="border-t border-leaf-100">
                        <td className="py-2 text-leaf-700">
                          {c.createdAt.toLocaleDateString("es-CL")}
                        </td>
                        <td className="py-2 font-mono text-xs text-leaf-900">{c.code}</td>
                        <td className="py-2 text-right text-leaf-700">{formatCLP(c.saleAmount)}</td>
                        <td className="py-2 text-right font-semibold text-leaf-900">
                          {formatCLP(c.commissionAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </main>
    </>
  );
}

function StatCard({
  label,
  value,
  highlight = false
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col rounded-2xl p-3 ring-1 ${
        highlight
          ? "bg-leaf-700 text-white ring-leaf-800"
          : "bg-white text-leaf-950 ring-leaf-100"
      }`}
    >
      <p
        className={`text-[10px] uppercase tracking-wider ${
          highlight ? "text-white/80" : "text-leaf-600"
        }`}
      >
        {label}
      </p>
      <p className="mt-1 font-display text-lg font-semibold leading-tight">{value}</p>
    </div>
  );
}

function EmptyBox({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-leaf-200 bg-leaf-50/40 px-4 py-5 text-center">
      <p className="text-sm font-semibold text-leaf-900">{title}</p>
      <p className="mt-1 text-xs text-leaf-700">{body}</p>
    </div>
  );
}
